<template>
	<BaseCatalogDetail v-slot="{item}">
		<BaseCatalogProductsWidget :fetch="{related_code: 'related', related_item_id: item.id, limit: 12, related_widget_data: item.related_widget_data}" v-slot="{items: relatedItems}">
			<div class="cd-main">
				<div class="wrapper cd-wrapper">
					<div class="cd-col cd-col1"><CmsBreadcrumbs :items="item.breadcrumbs" class="cd-bc" mode="catalogDetail" /></div>
					<div class="cd-col2" v-if="!mobileBreakpoint"></div>
				</div>	
				<div class="wrapper cd-wrapper">
					<div class="cd-col cd-col1">

						<div class="cd-images" v-interpolation>
							<template v-if="item.images?.length">
								<BaseUiSwiper class="cd-hero-slider" :options="{effect: 'fade', fadeEffect: {crossFade: true}, navigation: {enabled: false}}" :thumbs-swiper="thumbsSwiper">
									<BaseUiSwiperSlide v-for="(file, index) in item.images" :key="index" class="cd-hero-slide">
										<span :data-index="index" :data-thumb="file.file_thumbs?.['width180-height180']?.thumb">
											<BaseUiImage :title="file.title" :alt="file.description || item.title" loading="lazy" :data="file.file_thumbs?.['width600-height600']" default="/images/no-image-600.jpg" />
										</span>
									</BaseUiSwiperSlide>
								</BaseUiSwiper>
								<ClientOnly>
									<BaseUiSwiper 
										v-if="item.images?.length > 1" 
										class="cd-thumbs cd-thumbs-slider" 
										:class="{'has-navigation': item.images?.length > 4}" 
										:options="{
											slidesPerView: 10, 
											slidesPerGroup: 10, 
											watchSlidesProgress: true, 
											spaceBetween: 0, 
											breakpoints: {
												980: {
													enabled: true,
													spaceBetween: 16,
													slidesPerView: 4,
													slidesPerGroup: 4,
												}
											}
										}" 
										@init="setThumbsSwiper"
									>
										<BaseUiSwiperSlide v-for="(thumb, index) in item.images" :key="index" class="cd-thumb">
											<span><BaseUiImage loading="lazy" :title="thumb.title" :alt="thumb.description || item.title + ' ' + Number(index + 2)" :data-index="index" :data="thumb.file_thumbs?.['width180-height180']" default="/images/no-image-80.jpg" /></span>
										</BaseUiSwiperSlide>
									</BaseUiSwiper>
								</ClientOnly>
							</template>
							<template v-else>
								<div class="cd-hero-image cd-no-image">
									<BaseUiImage src="/images/no-image-600.jpg" :title="item.seo_h1" :alt="item.seo_h1" />
								</div>
							</template>
						</div>
					</div>
					<div class="cd-col cd-col2">
						<div class="cd-badges" v-if="item?.status != 3">
							<template v-if="+item.discount_percent_custom">
								<div class="cd-badge cd-badge-discount">%</div>
							</template>
							<template v-else>
								<BaseCmsLabel code="badge_new" tag="div" class="cd-badge cd-badge-new" v-if="item?.priority_details?.code == 'new'" />
							</template>
							<div class="cd-badge cd-special-attribute" v-if="item.attributes_special && item.attributes_special[0].attribute_code == 'posebne_oznake'">{{item.attributes_special[0].title}}</div>
						</div>

						<NuxtLink :to="item.category_url_without_domain" class="cd-category-title">{{item.category_title}}</NuxtLink>
						<h1 class="cd-title">{{item.title}}</h1>
						<div class="cd-code"><BaseCmsLabel code="code" />: {{item.code}}</div>

						<div class="cd-short-description-container" v-if="item.short_description">
							<div class="cd-short-description" v-html="item.short_description" />
							<BaseCmsLabel code="more_info" tag="div" class="cd-content-link" v-if="item?.content" @click="toTab()" />
						</div>	
						
						<div class="cd-price-container" :class="{'not-available': !item.is_available}" v-if="item?.status != 3">
							<template v-if="item.discount_percent_custom">
								<div class="cd-price">
									<!--<div class="cd-old-price"><BaseUtilsFormatCurrency :price="item.basic_price_custom" /></div>-->
									<BaseCmsLabel code="price" tag="div" class="cd-price-label" />
									<div class="cd-current-price red"><BaseUtilsFormatCurrency :price="item.price_custom" /></div>
								</div>
							</template>
							<template v-else>
								<div class="cd-price">
									<BaseCmsLabel code="price" tag="div" class="cd-price-label" />
									<div class="cd-current-price"><BaseUtilsFormatCurrency :price="item.price_custom" /></div>
								</div>
							</template>

							<div class="cd-price-note">
								<div class="cd-lowest-price" v-if="item.extra_price_lowest > 0 && item.basic_price > item.price_custom">
									<BaseCmsLabel code="lowest_price" />: <strong><BaseUtilsFormatCurrency :price="item.extra_price_lowest" /></strong>
								</div>
								<BaseCmsLabel code="tax_info" tag="div" class="cd-tax" :replace="[{'%VALUE%': item.tax * 100}]" />
								<div class="cd-additional-info" v-html="item.element_additional_info" v-if="item?.element_additional_info" />
							</div>
						</div>

						<div class="cd-included-note" v-if="item?.element_price_included">
							<BaseCmsLabel code="price_includec_title" tag="div" class="cd-included-note-title" />
							<div class="cd-included-note-content" v-html="item.element_price_included" />
						</div>

						<div id="cd-add-to-cart-container" ref="addToCartContainer">
							<WebshopAddToCartContainer :item="item" />
						</div>

						<div class="cd-share" v-if="!item.content && !item.element_characteristics && !item.attributes?.length && !item.documents?.length">
							<CmsShare />
						</div>
					</div>
				</div>
			</div>
			
			<CatalogDetailContent :item="item" :relatedItems="relatedItems"  v-if="item.content || item.element_characteristics || item.attributes?.length || item.documents?.length || relatedItems?.length" ref="activeContent" />
		</BaseCatalogProductsWidget>
	</BaseCatalogDetail>
</template>

<script setup>
	const {mobileBreakpoint} = inject('rwd');
	const {scrollTo} = useDom();
	const thumbsSwiper = ref(null);
	const setThumbsSwiper = (swiper) => {
		thumbsSwiper.value = swiper;
	};

	const activeContent = ref(null);
	function toTab(){
		scrollTo('#tab-desc', {offset: 80});
		activeContent.value?.activate()
	}
</script>


<style lang="less" scoped>
	.cd-main{
		position: relative; display: block; border-top: 1px solid var(--gray); padding: 24px 0 40px;
		@media (max-width: @m){padding-bottom: 25px; border-top: 0;}	
	}
	:deep(.cd-bc){
		a{
			color: var(--blue);
			&:last-of-type{
				padding-right: 0; margin-right: 0;
				&:after{display: none;}
			}
		}
		&>span{display: none;}
		@media (max-width: @m){
			overflow-x: auto; width: 100%; position: relative;
			&:before{.pseudo(51px,20px); top: 0; left: 0; background-image: linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%); z-index: 1;}
			
		}
	}
	:deep(.cd-bc-items){
		display: flex; row-gap: 8px; flex-wrap: wrap;
		@media (min-width: @m){white-space: nowrap; flex-wrap: inherit;}
		@media (max-width: @m){
			display: flex; flex-wrap: inherit; overflow-x: auto; width: 100%;
			&>a, &>span{flex-grow: 0; flex-shrink: 0;}	
			&::-webkit-scrollbar {-webkit-appearance: none; height: 0;}
			&::-webkit-scrollbar-thumb {background-color: transparent;}
		}
	}
	.cd-wrapper{
		display: flex; justify-content: center;
		@media (min-width: @l){padding: 0; gap: 120px;}
		@media (max-width: @l){gap: 30px;}
		@media (max-width: @m){flex-flow: column; gap: 24px;}
	}
	.cd-col1{
		width: 600px; flex-grow: 0; flex-shrink: 0;
		@media (max-width: @l){width: 520px;}	
		@media (max-width: @m){width: 100%;}
	}
	.cd-no-image{
		@media (max-width: @m){display: flex; width: 100%; align-items: center; justify-content: center;}
	}
	.cd-thumbs{
		position: relative; display: block; margin-top: 16px;
		&.has-navigation{padding: 0 65px;}
		&:not(.has-navigation){
			@media (min-width: @m){	
				.cd-thumb{width: calc(~"20% - 22px")!important;}
			}
		}	
		@media (max-width: @m){
			margin-top: 0;
			&.has-navigation{padding: 0;}
			:deep(.swiper-wrapper){display: flex; flex-wrap: wrap; height: auto; gap: 12px;}
		}
		:deep(.swiper-button){
			width: 44px; height: 44px; background: var(--blue2); box-shadow: none;
			&:after{font-size: 11px; color: #fff;}	
		}
		:deep(.swiper-button-prev){left: 0;}
		:deep(.swiper-button-next){right: 0;}
	}
	.cd-thumb{
		position: relative; display: flex; align-items: center; justify-content: center; border: 1px solid transparent; border-radius: 10px; padding: 4px; cursor: pointer; .transition(border-color);
		@media (min-width: @h){
			&:hover{border-color: var(--blue);}
		}
		:deep(img){display: block; width: auto; height: auto; aspect-ratio: 1;}
		&.swiper-slide-thumb-active{border-color: var(--blue);}
		@media (max-width: @m){width: calc(~"20% - 9.6px")!important; flex-grow: 0; flex-shrink: 0; border-radius: 6px;}
	}
	.cd-col2{
		width: 520px; flex-grow: 0; flex-shrink: 0; padding-top: 24px;
		@media (max-width: @l){width: auto; flex-grow: 1; flex-shrink: 1;}	
		@media (max-width: @m){padding-top: 0;}
	}
	.cd-badges{position: relative; display: flex; gap: 8px;}
	.cd-badge{
		display: block; margin-bottom: 12px; font-size: 14px; line-height: 16px; padding: 7px 12px; border-radius: 3px;
		@media (max-width: @m){font-size: 12px; line-height: 14px; margin-bottom: 8px; padding: 6px 9px;}	
	}
	.cd-badge-discount{border: 1px solid var(--red); color: var(--red);}
	.cd-badge-new{border: 1px solid var(--blue); color: var(--blue); font-weight: 700;}
	.cd-special-attribute{background: var(--blue); color: #fff; font-weight: 700;}
	.cd-category-title{
		display: block; font-size: 14px; line-height: 20px; color: var(--blue); font-weight: 700; text-decoration-color: transparent; margin-bottom: 5px; .transition(text-decoration-color);
		@media (min-width: @h){
			&:hover{text-decoration-color: var(--blue);}
		}
		@media (max-width: @m){margin-bottom: 3px;}
	}
	.cd-title{
		display: block; font-size: 26px; line-height: 32px; padding-bottom: 0; margin-bottom: 6px;
		@media (max-width: @m){font-size: 18px; line-height: 1.33;}	
	}
	.cd-code{
		display: block; font-size: 12px; line-height: 14px; color: var(--darkGray); margin-bottom: 22px;
		@media (max-width: @m){margin-bottom: 16px;}	
	}
	.cd-short-description-container{
		position: relative; display: block; margin-bottom: 30px;
		@media (max-width: @m){margin-bottom: 22px;}	
	}
	.cd-short-description{
		display: block; font-size: 14px; line-height: 1.58;
		:deep(p){padding-bottom: 0;}
		@media (max-width: @m){font-size: 13px;}
	}
	.cd-content-link{
		display: block; font-size: 14px; line-height: 1.58; font-weight: 700; color: var(--blue); text-decoration: underline; margin-top: 3px; cursor: pointer; .transition(text-decoration-color);
		@media (min-width: @h){
			&:hover{text-decoration-color: transparent;}
		}
		@media (max-width: @m){font-size: 13px;}
	}
	.cd-price-container{
		position: relative; display: flex; flex-flow: column; margin-bottom: 30px;
		@media (max-width: @m){margin-bottom: 22px;}	
	}
	.cd-price-label{
		display: block; font-size: 12px; line-height: 14px; margin-bottom: 3px;
		@media (max-width: @m){line-height: 18px;}
	}
	.cd-current-price{
		display: block; font-size: 26px; line-height: 34px; font-weight: 700; margin-bottom: 2px;
		@media (max-width: @m){font-size: 22px; line-height: 24px;}	
	}
	.cd-price-note{position: relative; display: flex; flex-flow: column; font-size: 12px; line-height: 1.35; gap: 3px;}
	.cd-additional-info{
		display: flex; flex-flow: column; gap: 3px;
		:deep(p){padding-bottom: 0;}
	}
	.cd-included-note{
		position: relative; display: flex; flex-flow: column; justify-content: center; border: 1px solid var(--gray); border-radius: 8px; width: 100%; padding: 30px 30px 30px 96px; font-size: 14px; line-height: 1.3; margin-bottom: 24px;
		&:before{.pseudo(56px,56px); background: var(--lightGray); border-radius: 50%; left: 24px; display: flex; align-items: center; justify-content: center; .icon-delivery-box(); font: 23px/1 var(--fonti); color: var(--blue); text-indent: 4px;}
		:deep(p){padding-bottom: 0;}
		@media (max-width: @t){
			padding: 18px 18px 18px 82px;
			&:before{width: 48px; height: 48px; left: 18px; font-size: 20px; top: 20px;}
		}
		@media (max-width: @m){
			padding: 20px 20px 20px 80px; font-size: 12px; line-height: 16px; margin-bottom: 22px;
			&:before{width: 52px; height: 52px; left: 16px; font-size: 25px; top: 20px;}
		}
	}
	.cd-included-note-title{
		font-size: 14px; padding-bottom: 2px; font-weight: 700;
		:deep(p){padding-bottom: 0;}
		@media (max-width: @m){font-size: 13px;}
	}
</style>